import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useJournalEntryDetail } from '../../hooks/useRunExplorer';
import type { CollaborationJournal } from '../../types';
import type { BranchNode, CollaborationDefinition, CollaborationNode } from '../../types/definition';
import type { BranchEvaluation, RunLink, Turn, TurnStep } from '../../types/runExplorer';
import {
  branchEvaluationsOf,
  childLinkOf,
  entryStartMs,
  outputConsumers,
  tokenCount,
} from '../../utils/runTurnModel';
import {
  Collapsible,
  JsonBlock,
  STEP_STATE_STYLES,
  formatClock,
  formatCount,
  formatMs,
  getStatusBadgeClass,
  runErrorText,
  summarizeValue,
} from './runExplorerShared';
import { runExplorerPath } from './runExplorerLinks';
import type { RunSelection } from './RunTimeline';
import { failoverFromText, failoverOf } from '../../utils/journalFailover';

interface StepDetailPanelProps {
  collaborationId: string;
  turn: Turn | null;
  selection: RunSelection | null;
  definition: CollaborationDefinition | null;
  outputMapping: Record<string, string> | null;
  children: RunLink[];
  onSelect: (selection: RunSelection) => void;
}

const MODEL_CONFIG_SOURCE_LABELS: Record<string, string> = {
  'node-override': 'set on this step',
  'agent-default': "the agent's default",
  'agent-failover': "the agent's failover",
  environment: 'the environment default',
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-gray-900 dark:text-gray-100 min-w-0 break-words">{children}</dd>
    </div>
  );
}

/** A large text field of one journal entry, fetched the first time its section opens. */
function LargeField({
  collaborationId,
  entry,
  field,
  title,
  testId,
}: {
  collaborationId: string;
  entry: CollaborationJournal | null;
  field: 'prompt' | 'response' | 'output';
  title: string;
  testId: string;
}) {
  const [requested, setRequested] = useState(false);
  const { data, isLoading, error } = useJournalEntryDetail(collaborationId, entry?.id, requested);
  if (!entry) return null;
  const value = data?.[field];
  return (
    <Collapsible title={title} onOpen={() => setRequested(true)} testId={testId}>
      {isLoading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Loading…</p>
      ) : error ? (
        <p className="text-xs text-red-600 dark:text-red-400">Could not load this field.</p>
      ) : value === null || value === undefined || value === '' ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Nothing recorded.</p>
      ) : (
        <JsonBlock value={value} />
      )}
    </Collapsible>
  );
}

function InputsTable({ node, step }: { node: CollaborationNode | undefined; step: TurnStep }) {
  const inputs = asRecord(step.resultEntry?.input) ?? asRecord(step.nodeData?.input) ?? {};
  const mapping = node?.inputMapping ?? {};
  const fields = Array.from(new Set([...Object.keys(mapping), ...Object.keys(inputs)]));
  if (fields.length === 0) return <p className="text-xs text-gray-500 dark:text-gray-400">No inputs.</p>;
  return (
    <table className="w-full text-xs" data-testid="detail-inputs">
      <thead>
        <tr className="text-left text-gray-500 dark:text-gray-400">
          <th className="py-1 pr-2 font-medium">Field</th>
          <th className="py-1 pr-2 font-medium">Mapping</th>
          <th className="py-1 font-medium">Value</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={field} className="border-t border-gray-100 dark:border-gray-700 align-top">
            <td className="py-1 pr-2 font-mono text-gray-800 dark:text-gray-200">{field}</td>
            <td className="py-1 pr-2 font-mono text-gray-600 dark:text-gray-300 break-all">
              {mapping[field] ?? <span className="text-gray-400">not mapped on this step</span>}
            </td>
            <td className="py-1 text-gray-700 dark:text-gray-200 break-words">{summarizeValue(inputs[field])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SchemaCheckSection({
  step,
  definition,
  outputMapping,
}: {
  step: TurnStep;
  definition: CollaborationDefinition | null;
  outputMapping: Record<string, string> | null;
}) {
  const output = asRecord(step.resultEntry?.output) ?? {};
  const consumers = outputConsumers(definition, step.stepId, outputMapping);
  const fields = Array.from(
    new Set([...Object.keys(output), ...consumers.map((c) => c.field).filter((f): f is string => !!f)])
  );
  const check = step.schemaCheck;
  const outputCheck = step.outputCheck;
  let verdict: ReactNode;
  if (check) {
    verdict = check.passed ? (
      <span className="text-green-700 dark:text-green-300">Output matches the schema.</span>
    ) : (
      <span className="text-amber-700 dark:text-amber-300">
        Output does not match the schema.
        {check.missingFields?.length ? ` Missing: ${check.missingFields.join(', ')}.` : ''}
        {check.mistypedFields?.length
          ? ` Wrong type: ${check.mistypedFields.map((f) => `${f.field} (expected ${f.expected}, got ${f.actual})`).join(', ')}.`
          : ''}{' '}
        The step failed: the reply did not match its output contract after the correction retry. The missing or
        mistyped fields are listed below.
      </span>
    );
  } else if (outputCheck) {
    verdict = outputCheck.passed ? (
      <span className="text-green-700 dark:text-green-300">The reply passed the output check ({outputCheck.tier ?? 'checked'}).</span>
    ) : (
      <span className="text-amber-700 dark:text-amber-300">
        The reply failed the output check
        {outputCheck.reason === 'parse' ? ': it could not be read as one JSON object' : ''}.
        {outputCheck.errors?.length
          ? ` ${outputCheck.errors.map((e) => [e.field, e.error].filter(Boolean).join(': ')).join('; ')}.`
          : ''}
      </span>
    );
  } else {
    verdict = (
      <span className="text-gray-500 dark:text-gray-400">
        No schema check was recorded for this step (the run predates schema checks).
      </span>
    );
  }

  return (
    <div className="space-y-2" data-testid="detail-schema-check">
      <p className="text-sm">{verdict}</p>
      {fields.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500 dark:text-gray-400">
              <th className="py-1 pr-2 font-medium">Output field</th>
              <th className="py-1 pr-2 font-medium">Present</th>
              <th className="py-1 font-medium">Read by</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const readers = consumers.filter((c) => c.field === field);
              return (
                <tr key={field} className="border-t border-gray-100 dark:border-gray-700 align-top">
                  <td className="py-1 pr-2 font-mono">{field}</td>
                  <td className="py-1 pr-2">
                    {field in output ? 'yes' : <span className="text-amber-700 dark:text-amber-300">missing</span>}
                  </td>
                  <td className="py-1">
                    {readers.length === 0
                      ? <span className="text-gray-400">nobody</span>
                      : readers
                          .map((r) => (r.consumerStepId ? `${r.consumerStepId}.${r.target}` : `reply field ${r.target}`))
                          .join(' · ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <Collapsible title="Parsed output">
        <JsonBlock value={step.resultEntry?.output ?? null} />
      </Collapsible>
    </div>
  );
}

function DownstreamSection({
  step,
  turn,
  definition,
  onSelect,
}: {
  step: TurnStep;
  turn: Turn;
  definition: CollaborationDefinition | null;
  onSelect: (selection: RunSelection) => void;
}) {
  const consumerIds = Array.from(
    new Set(
      outputConsumers(definition, step.stepId)
        .map((c) => c.consumerStepId)
        .filter((id): id is string => !!id)
    )
  );
  if (consumerIds.length === 0) return <p className="text-xs text-gray-500 dark:text-gray-400">No step reads this output.</p>;
  return (
    <ul className="space-y-1 text-xs" data-testid="detail-downstream">
      {consumerIds.map((id) => {
        const consumer = turn.steps[id];
        const state = consumer?.state ?? 'not-reached';
        return (
          <li key={id} className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => onSelect({ stepId: id })} className="font-mono text-indigo-600 dark:text-indigo-400 hover:underline">
              {id}
            </button>
            <span className={`px-1.5 py-0.5 rounded ${STEP_STATE_STYLES[state].badge}`}>{STEP_STATE_STYLES[state].label}</span>
            {consumer?.resultEntry?.output && (
              <span className="text-gray-600 dark:text-gray-300">{summarizeValue(consumer.resultEntry.output)}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ToolCallsTable({ step, children, onSelect }: { step: TurnStep; children: RunLink[]; onSelect: (s: RunSelection) => void }) {
  if (step.toolCalls.length === 0) return null;
  return (
    <Collapsible title={`Tool calls, in order (${step.toolCalls.length})`} defaultOpen>
      <table className="w-full text-xs" data-testid="detail-tool-calls">
        <tbody>
          {step.toolCalls.map((call, i) => {
            const child = childLinkOf(call, children);
            return (
              <tr key={call.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-1 pr-2 text-gray-500">{i + 1}</td>
                <td className="py-1 pr-2">
                  <button type="button" onClick={() => onSelect({ stepId: step.stepId, entryId: call.id })} className="font-mono text-violet-700 dark:text-violet-300 hover:underline">
                    {call.functionName ?? 'tool'}
                  </button>
                </td>
                <td className="py-1 pr-2 text-gray-500">{formatMs(call.duration)}</td>
                <td className="py-1">
                  {call.error ? (
                    <span className="text-red-600 dark:text-red-400">error</span>
                  ) : child ? (
                    <Link to={runExplorerPath(child.collaborationId, child.runId)} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                      child run {child.runId.slice(0, 8)}
                    </Link>
                  ) : (
                    <span className="text-gray-500">ok</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Collapsible>
  );
}

function AgentDetail({ collaborationId, step, node, turn, definition, outputMapping, children, onSelect }: {
  collaborationId: string;
  step: TurnStep;
  node: CollaborationNode | undefined;
  turn: Turn;
  definition: CollaborationDefinition | null;
  outputMapping: Record<string, string> | null;
  children: RunLink[];
  onSelect: (selection: RunSelection) => void;
}) {
  const call = step.modelCalls[step.modelCalls.length - 1] ?? null;
  const result = step.resultEntry;
  const source = call ?? result;
  const sum = (key: string) => step.modelCalls.reduce((total, c) => total + tokenCount(c.usage, key), 0);
  const inputTokens = sum('inputTokens');
  const cacheRead = sum('cacheReadTokens');
  const cost = step.modelCalls.reduce((total, c) => {
    const value = c.usage?.cost ?? c.metadata?.cost;
    return typeof value === 'number' ? (total ?? 0) + value : total;
  }, null as number | null);
  const history = typeof result?.metadata?.userMessage === 'string' ? result.metadata.userMessage : null;
  // The step's completed or failed entry carries the failover record when the
  // step ran on the agent's failover Model Config (task 1300/1301).
  const failover =
    step.entries
      .filter((entry) => entry.event === 'completed' || entry.event === 'failed')
      .map((entry) => failoverOf(entry.metadata))
      .find(Boolean) ?? null;
  const temperature = source?.llmTemperature;

  return (
    <div className="space-y-3">
      <dl className="space-y-1">
        <Fact label="Agent">
          {source?.agentId ? (
            <Link to={`/agents/${source.agentId}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
              {source.agent?.name ?? source.agentId.slice(0, 8)}
            </Link>
          ) : '—'}
        </Fact>
        <Fact label="Model Config">
          {source?.modelConfig?.name ?? (source?.modelConfigId ? source.modelConfigId.slice(0, 8) : '—')}
          {source?.modelConfigSource && (
            <span className="text-gray-500 dark:text-gray-400"> ({MODEL_CONFIG_SOURCE_LABELS[source.modelConfigSource] ?? source.modelConfigSource})</span>
          )}
        </Fact>
        <Fact label="Model">
          <span className="font-mono">{source?.llmModel ?? '—'}</span>
          {failover && (
            <span
              data-testid="step-failover-mark"
              title={failover.primaryError ?? undefined}
              className="ml-2 inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
            >
              Ran on failover {failoverFromText(failover)}
            </span>
          )}
          {' · '}temperature {temperature == null ? 'not sent' : temperature}
          {source?.llmMaxOutputTokens != null && ` · output limit ${formatCount(source.llmMaxOutputTokens)}`}
        </Fact>
        <Fact label="Iterations">
          {typeof result?.metadata?.iterations === 'number' ? result.metadata.iterations : step.modelCalls.length}
          {typeof result?.metadata?.stoppingReason === 'string' && ` · stopped: ${result.metadata.stoppingReason}`}
        </Fact>
        {call && (
          <Fact label="Finish">
            {String(call.metadata?.finishReason ?? '—')}
            {typeof call.metadata?.responseId === 'string' && <> · provider id <span className="font-mono">{call.metadata.responseId}</span></>}
            {typeof call.metadata?.responseModelId === 'string' && <> · served <span className="font-mono">{call.metadata.responseModelId}</span></>}
          </Fact>
        )}
        <Fact label="Tokens">
          <span data-testid="detail-tokens">
            prompt {formatCount(inputTokens)} · completion {formatCount(sum('outputTokens'))} · cache read {formatCount(cacheRead)} · cache write{' '}
            {formatCount(sum('cacheWriteTokens'))}
            {inputTokens > 0 && cacheRead > 0 && ` · ${Math.round((cacheRead / inputTokens) * 100)}% read from cache`}
          </span>
        </Fact>
        {cost !== null && <Fact label="Cost">${cost.toFixed(4)} at the Model's prices</Fact>}
      </dl>

      <Collapsible title="Inputs and where they came from" defaultOpen>
        <InputsTable node={node} step={step} />
      </Collapsible>
      {history && (
        <Collapsible title="Conversation history sent with the call" meta={`${history.length.toLocaleString()} chars`}>
          <JsonBlock value={history} />
        </Collapsible>
      )}
      <LargeField collaborationId={collaborationId} entry={call ?? result} field="prompt" title="Rendered prompt" testId="detail-rendered-prompt" />
      <LargeField collaborationId={collaborationId} entry={call ?? result} field="response" title="Raw reply" testId="detail-raw-reply" />
      <Collapsible title="Parsed output and schema check" defaultOpen={step.state === 'ran-with-problem'}>
        <SchemaCheckSection step={step} definition={definition} outputMapping={outputMapping} />
      </Collapsible>
      <ToolCallsTable step={step} children={children} onSelect={onSelect} />
      <Collapsible title="Downstream">
        <DownstreamSection step={step} turn={turn} definition={definition} onSelect={onSelect} />
      </Collapsible>
    </div>
  );
}

function FunctionDetail({ step, node, collaborationId }: { step: TurnStep; node: CollaborationNode | undefined; collaborationId: string }) {
  const fn = step.resultEntry?.functionName ?? (node && 'function' in node ? node.function : null);
  return (
    <div className="space-y-3">
      <dl className="space-y-1">
        <Fact label="Function"><span className="font-mono">{fn ?? '—'}</span></Fact>
      </dl>
      <Collapsible title="Inputs and where they came from" defaultOpen>
        <InputsTable node={node} step={step} />
      </Collapsible>
      <Collapsible title="Output" defaultOpen>
        <JsonBlock value={step.resultEntry?.output ?? step.nodeData?.output ?? null} />
      </Collapsible>
      {step.resultEntry && !step.resultEntry.output && (
        <LargeField collaborationId={collaborationId} entry={step.resultEntry} field="output" title="Full output" testId="detail-function-output" />
      )}
    </div>
  );
}

function BranchDetail({ step, node, turn, onSelect }: { step: TurnStep; node: CollaborationNode | undefined; turn: Turn; onSelect: (s: RunSelection) => void }) {
  const evaluations = branchEvaluationsOf(step.resultEntry);
  const output = asRecord(step.resultEntry?.output) ?? asRecord(step.nodeData?.output) ?? {};
  const target = typeof output.targetNodeId === 'string' ? output.targetNodeId : null;
  const selectedBranch = typeof output.selectedBranch === 'string' ? output.selectedBranch : null;
  const decisions = node?.type === 'branch' ? (node as BranchNode).decisions ?? [] : [];
  const skippedHere = turn.skipped.filter((id) => turn.steps[id]?.skip?.branchStepId === step.stepId);

  return (
    <div className="space-y-3">
      <dl className="space-y-1">
        <Fact label="Chose">
          {target ? <span className="font-mono">→ {target}</span> : '—'}
          {selectedBranch && <span className="text-gray-500 dark:text-gray-400"> ({selectedBranch})</span>}
        </Fact>
        <Fact label="Evaluated">{formatClock(step.endedAt, true)}</Fact>
      </dl>
      {evaluations ? (
        <EvaluationsTable evaluations={evaluations} decisions={decisions} />
      ) : (
        <div data-testid="detail-branch-degraded" className="space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This run was recorded before branch evaluations were kept, so the conditions below are the config's, without results.
          </p>
          <ol className="space-y-1 text-xs">
            {decisions.map((decision, i) => (
              <li
                key={decision.id}
                className={`rounded px-2 py-1 ${decision.id === selectedBranch ? 'bg-green-50 dark:bg-green-900/30 font-medium' : 'bg-gray-50 dark:bg-gray-800'}`}
              >
                {i + 1}. <span className="font-mono">{decision.condition ?? 'default'}</span> → <span className="font-mono">{decision.targetNodeId}</span>
                {decision.id === selectedBranch && ' (chosen)'}
              </li>
            ))}
          </ol>
        </div>
      )}
      {skippedHere.length > 0 && (
        <p className="text-xs text-gray-600 dark:text-gray-300" data-testid="detail-branch-consequence">
          Skipped by this decision:{' '}
          {skippedHere.map((id, i) => (
            <span key={id}>
              {i > 0 && ', '}
              <button type="button" onClick={() => onSelect({ stepId: id })} className="font-mono text-indigo-600 dark:text-indigo-400 hover:underline">{id}</button>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

function EvaluationsTable({ evaluations, decisions }: { evaluations: BranchEvaluation[]; decisions: NonNullable<BranchNode['decisions']> }) {
  const chosen = evaluations.find((e) => e.result && e.targetNodeId);
  return (
    <table className="w-full text-xs" data-testid="detail-branch-evaluations">
      <thead>
        <tr className="text-left text-gray-500 dark:text-gray-400">
          <th className="py-1 pr-2 font-medium">#</th>
          <th className="py-1 pr-2 font-medium">Condition</th>
          <th className="py-1 pr-2 font-medium">Evaluated</th>
          <th className="py-1 pr-2 font-medium">Result</th>
          <th className="py-1 font-medium">Target</th>
        </tr>
      </thead>
      <tbody>
        {evaluations.map((evaluation) => {
          const isChosen = evaluation === chosen;
          const decision = decisions[evaluation.conditionIndex];
          return (
            <tr
              key={evaluation.conditionIndex}
              data-chosen={isChosen || undefined}
              className={`border-t border-gray-100 dark:border-gray-700 align-top ${isChosen ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
            >
              <td className="py-1 pr-2">{evaluation.conditionIndex + 1}</td>
              <td className="py-1 pr-2">
                <span className="font-mono break-all">{evaluation.conditionExpression ?? 'default'}</span>
                {decision?.id && <span className="block text-gray-500 dark:text-gray-400">{decision.id}</span>}
                {evaluation.valuesRead && Object.keys(evaluation.valuesRead).length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-gray-500 dark:text-gray-400">values read</summary>
                    <JsonBlock value={evaluation.valuesRead} />
                  </details>
                )}
              </td>
              <td className="py-1 pr-2">{evaluation.evaluated ? 'yes' : 'no'}</td>
              <td className="py-1 pr-2">{evaluation.evaluated ? String(evaluation.result) : '—'}</td>
              <td className="py-1 font-mono">{evaluation.targetNodeId ?? '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ToolCallDetail({ collaborationId, call, children }: { collaborationId: string; call: CollaborationJournal; children: RunLink[] }) {
  const child = childLinkOf(call, children);
  return (
    <div className="space-y-3">
      <dl className="space-y-1">
        <Fact label="Tool"><span className="font-mono">{call.functionName ?? '—'}</span></Fact>
        <Fact label="Took">{formatMs(call.duration)}</Fact>
      </dl>
      {call.error && (
        <p className="text-sm text-red-700 dark:text-red-300" data-testid="detail-tool-error">{call.error}</p>
      )}
      <Collapsible title="Arguments" defaultOpen>
        <JsonBlock value={call.input ?? null} />
      </Collapsible>
      <LargeField collaborationId={collaborationId} entry={call} field="output" title="Result" testId="detail-tool-result" />
      {child && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3" data-testid="detail-child-run">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              Child run · {child.collaborationName ?? 'collaboration'}
            </h4>
            {child.status && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(child.status)}`}>{child.status}</span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
            Run <span className="font-mono">{child.runId.slice(0, 8)}</span>
            {child.startedAt && child.completedAt && ` · ${formatMs(Date.parse(child.completedAt) - Date.parse(child.startedAt))}`}
          </p>
          <Link
            to={runExplorerPath(child.collaborationId, child.runId)}
            className="mt-2 inline-block text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Open this run
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * What the service knows about the selected step (task 1151, step 9), by the
 * step's type: agent, function, branch, entry, a skipped step, or one tool
 * call inside an agent step. A failed step leads with its error in the same
 * sentence the Assistant uses (task 1145).
 */
export function StepDetailPanel({
  collaborationId,
  turn,
  selection,
  definition,
  outputMapping,
  children,
  onSelect,
}: StepDetailPanelProps) {
  const step = selection && turn ? turn.steps[selection.stepId] : undefined;
  if (!selection || !turn || !step) {
    return (
      <div data-testid="step-detail-empty" className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
        Select a step in the path or timeline.
      </div>
    );
  }
  const node = definition?.nodes?.find((n) => n.id === step.stepId);
  const toolCall = selection.entryId ? step.toolCalls.find((c) => c.id === selection.entryId) ?? null : null;
  const style = STEP_STATE_STYLES[step.state];
  const failure = step.entries.find((entry) => entry.event === 'failed')?.error ?? null;
  const start = step.entries.length ? Math.min(...step.entries.map(entryStartMs)) : NaN;

  return (
    <div data-testid="step-detail" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold font-mono text-gray-900 dark:text-white">
          {step.stepId}
          {toolCall && <span className="text-violet-700 dark:text-violet-300"> › {toolCall.functionName}</span>}
        </h3>
        <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
          {toolCall ? 'tool call' : step.nodeType}
        </span>
        <span data-testid="step-detail-state" className={`px-1.5 py-0.5 rounded text-xs font-medium ${style.badge}`}>{style.label}</span>
      </div>
      {step.entries.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatClock(Number.isFinite(start) ? new Date(start).toISOString() : step.startedAt, true)} →{' '}
          {formatClock(step.endedAt, true)} ({formatMs(step.durationMs)})
        </p>
      )}
      {failure && (
        <div data-testid="step-detail-failure" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded flex gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{runErrorText(failure)}</p>
        </div>
      )}
      {!failure && step.problems.length > 0 && (
        <ul data-testid="step-detail-problems" className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-sm text-amber-800 dark:text-amber-200 list-disc pl-6">
          {step.problems.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      )}

      {toolCall ? (
        <ToolCallDetail collaborationId={collaborationId} call={toolCall} children={children} />
      ) : step.state === 'skipped' ? (
        <dl className="space-y-1" data-testid="detail-skipped">
          <Fact label="Skipped by">
            {step.skip?.branchStepId ? (
              <button type="button" onClick={() => onSelect({ stepId: step.skip!.branchStepId! })} className="font-mono text-indigo-600 dark:text-indigo-400 hover:underline">
                {step.skip.branchStepId}
              </button>
            ) : 'a branch'}
          </Fact>
          <Fact label="Branch chosen"><span className="font-mono">{step.skip?.selectedBranch ?? '—'}</span></Fact>
          {step.skip?.reason && (
            <Fact label="Why">
              {step.skip.reason === 'upstream-skipped' ? 'a step before it was skipped' : step.skip.reason === 'branch' ? 'the branch routed away from it' : step.skip.reason}
            </Fact>
          )}
          <Fact label="At">{formatClock(step.skip?.at, true)}</Fact>
          {step.skip?.inferred && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Read from the step data: this run was recorded before skips were journaled.
            </p>
          )}
        </dl>
      ) : step.state === 'not-reached' ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">This step was not reached in this turn.</p>
      ) : step.nodeType === 'agent' ? (
        <AgentDetail
          collaborationId={collaborationId}
          step={step}
          node={node}
          turn={turn}
          definition={definition}
          outputMapping={outputMapping}
          children={children}
          onSelect={onSelect}
        />
      ) : step.nodeType === 'branch' ? (
        <BranchDetail step={step} node={node} turn={turn} onSelect={onSelect} />
      ) : step.nodeType === 'function' ? (
        <FunctionDetail step={step} node={node} collaborationId={collaborationId} />
      ) : (
        <div className="space-y-3">
          <Collapsible title="Turn input" defaultOpen>
            <JsonBlock value={step.resultEntry?.input ?? step.nodeData?.input ?? null} />
          </Collapsible>
        </div>
      )}
    </div>
  );
}
